import React, { useEffect, useState } from "react";
import api from "../api/client";
import ModalBackdrop from "./ModalBackdrop";

/**
 * Merge several selected tickets into one, chosen from among them.
 *
 * WHY THIS LIVES ON THE LIST. The ticket list is where a human NOTICES two
 * tickets are the same thing — three rows with near-identical subjects from
 * the same person inside an hour. Making them open one, remember the other's
 * number, and merge from the detail page is asking them to carry state in
 * their head between screens, which is where the old prompt-based flow lost
 * people entirely.
 *
 * THE SURVIVOR IS PICKED, NOT INFERRED. "Merge these four" has no obvious
 * answer to "into which one". Guessing — lowest id, oldest, most replies —
 * would be a rule nobody can see and everybody is eventually surprised by, so
 * the choice is a radio button with the consequence spelled out next to it.
 *
 * ALL OR NOTHING. One request, one transaction. Four sequential merges that
 * fail on the third would leave two tickets gone and two live, with nothing
 * on the page to say which.
 */

type Row = { id: number; title: string | null; status: string | null };

type BatchPreview = {
  target_id: number;
  target_title: string | null;
  source_ids: number[];
  per_ticket: { source_id: number; source_title: string | null; moves: string[] }[];
  counts: Record<string, number>;
  moves: string[];
  target_will_reopen: boolean;
};

export default function TicketMergeBatchModal({
  tickets,
  onClose,
  onMerged,
}: {
  /** The rows the user ticked. At least two. */
  tickets: Row[];
  onClose: () => void;
  onMerged: (survivorId: number) => void;
}) {
  // Default to the oldest (lowest id) because it is usually the original the
  // others duplicate — but it is only a DEFAULT, visibly selected, and every
  // other row is one click away.
  const [targetId, setTargetId] = useState<number>(
    () => tickets.map((t) => t.id).sort((a, b) => a - b)[0]
  );
  const [preview, setPreview] = useState<BatchPreview | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [merging, setMerging] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setPreviewing(true);
    setError("");
    api
      .post("/api/v1/tickets/merge-batch-preview", {
        source_ids: tickets.map((t) => t.id),
        target_id: targetId,
      })
      .then((r) => {
        if (!cancelled) setPreview(r.data);
      })
      .catch((e: any) => {
        if (cancelled) return;
        setPreview(null);
        setError(e?.response?.data?.detail || "That merge is not possible.");
      })
      .finally(() => {
        if (!cancelled) setPreviewing(false);
      });
    return () => {
      cancelled = true;
    };
  }, [targetId, tickets]);

  const doMerge = async () => {
    setMerging(true);
    setError("");
    try {
      const r = await api.post("/api/v1/tickets/merge-batch", {
        source_ids: tickets.map((t) => t.id),
        target_id: targetId,
      });
      onMerged(r.data.target_id);
    } catch (e: any) {
      // The server rolls the whole batch back, so the list is unchanged and it
      // is safe to say so — otherwise the user is left wondering how much of
      // it went through.
      setError(
        (e?.response?.data?.detail || "That merge could not be completed.") +
          " Nothing was merged."
      );
      setMerging(false);
    }
  };

  const losing = tickets.filter((t) => t.id !== targetId);

  return (
    <ModalBackdrop onClose={merging ? () => {} : onClose}>
      <div
        className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl max-h-[88vh] flex flex-col"
        role="dialog"
        aria-modal="true"
        aria-label="Merge selected tickets"
      >
        <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Merge {tickets.length} tickets
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Choose the one to keep. The other {losing.length === 1 ? "one" : losing.length}{" "}
            will be closed and everything on {losing.length === 1 ? "it" : "them"} moves
            across.
          </p>
        </div>

        {error && (
          <div className="mx-5 mt-4 px-3 py-2 rounded text-sm bg-red-50 dark:bg-red-900/40 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-300">
            {error}
          </div>
        )}

        <div className="flex-1 overflow-y-auto px-5 py-3">
          {tickets.map((t) => {
            const keep = t.id === targetId;
            return (
              <label
                key={t.id}
                className={`flex items-start gap-3 px-3 py-2 rounded border mb-1.5 cursor-pointer ${
                  keep
                    ? "border-green-500 bg-green-50 dark:bg-green-900/25"
                    : "border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/40"
                }`}
              >
                <input
                  type="radio"
                  name="merge-target"
                  checked={keep}
                  onChange={() => setTargetId(t.id)}
                  className="mt-1"
                />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs text-gray-500 dark:text-gray-400">
                      #{t.id}
                    </span>
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                      {t.title || "(no subject)"}
                    </span>
                  </div>
                  <div
                    className={`text-xs mt-0.5 ${
                      keep
                        ? "text-green-700 dark:text-green-400 font-medium"
                        : "text-gray-500 dark:text-gray-400"
                    }`}
                  >
                    {keep
                      ? "KEEP — everything moves here"
                      : "will be closed and merged in"}
                  </div>
                </div>
              </label>
            );
          })}
        </div>

        <div className="px-5 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 text-sm">
          {previewing && (
            <div className="text-gray-500 dark:text-gray-400">
              Checking what would move…
            </div>
          )}
          {!previewing && preview && (
            <>
              <div className="text-gray-900 dark:text-gray-100">
                {losing.map((t) => `#${t.id}`).join(", ")} will be closed and merged into{" "}
                <span className="font-semibold">#{preview.target_id}</span>.
              </div>
              <div className="mt-1 text-gray-600 dark:text-gray-400">
                {preview.moves.length
                  ? "Moving in total: " + preview.moves.join(", ") + "."
                  : "Nothing is attached to the tickets being merged in."}
              </div>
              {preview.target_will_reopen && (
                <div className="mt-1 text-gray-600 dark:text-gray-400">
                  #{preview.target_id} is closed and will be reopened.
                </div>
              )}
              <div className="mt-1 text-gray-500 dark:text-gray-400">
                This cannot be undone from here.
              </div>
            </>
          )}
        </div>

        <div className="px-5 py-3 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2">
          <button
            onClick={onClose}
            disabled={merging}
            className="px-3 py-1.5 text-sm rounded border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/40 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={doMerge}
            disabled={!preview || previewing || merging}
            className="px-3 py-1.5 text-sm rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {merging
              ? "Merging…"
              : `Merge ${losing.length} into #${targetId}`}
          </button>
        </div>
      </div>
    </ModalBackdrop>
  );
}
